import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderOpenIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useProject } from './ProjectContext';

const MotionPaper = motion(Paper);

const pillSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 750,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.26)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.32)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    color: alpha('#e9e7ff', 0.42),
    boxShadow: 'none',
  },
};

const tableShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${alpha('#fff', 0.1)}`,
  backgroundColor: alpha('#000', 0.22),
};

const headCellSx = {
  fontWeight: 800,
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: alpha('#0a0c12', 0.96),
  borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
  color: alpha('#e9e7ff', 0.65),
};

function projectFallbackColor(input) {
  const s = (input || '').toString();
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 70% 55% / 1)`;
}

function formatDate(value) {
  try {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch (e) {
    return '—';
  }
}

/** Short link label for tables; full URL stays on href + title. */
function shortExternalLinkLabel(url) {
  const raw = (url || '').toString().trim();
  if (!raw) return '';
  try {
    const u = new URL(raw);
    const segments = u.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || '';
    if (/^0x[a-fA-F0-9]{40}$/i.test(last)) {
      const a = last.slice(0, 6);
      const b = last.slice(-4);
      return u.hostname.includes('dextools.io') ? `DexTools · ${a}…${b}` : `${a}…${b}`;
    }
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname.length > 18 ? `${u.pathname.slice(0, 14)}…` : u.pathname;
    return `${host}${path || '/'}`;
  } catch {
    if (raw.length <= 36) return raw;
    return `${raw.slice(0, 16)}…${raw.slice(-12)}`;
  }
}

/** Under project title: DexTools URL when launch-complete + pair resolved; else readiness copy. */
function ProjectSubtitleRow({ project: p, accentCyan }) {
  const launchDone = !!(p.launch_completed_at || p.launchCompletedAt);
  const dexUrl = (p.dextools_url || p.dextoolsUrl || '').toString().trim();

  if (launchDone && dexUrl) {
    const linkLabel = shortExternalLinkLabel(dexUrl);
    return (
      <Typography
        variant="caption"
        component="a"
        href={dexUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={dexUrl}
        sx={{
          display: 'block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: alpha(accentCyan, 0.95),
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {linkLabel}
      </Typography>
    );
  }
  if (launchDone) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        Launch completed — DexTools link unavailable for this pool
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary" noWrap>
      {p.has_master_wallet ? 'Master wallet ready' : 'Needs setup'}
    </Typography>
  );
}

export default function ProjectsDashboard({ onOpenProject }) {
  const theme = useTheme();
  const accentPurple = theme.palette.primary.main;
  const accentCyan = theme.palette.secondary.main;

  const { projects, createProject, deleteProject, loadingProjects, refreshProjects } = useProject();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPictureUrl, setNewPictureUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...projects];

    if (tab === 'recent') {
      list = list
        .slice()
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);
    }

    if (q) {
      list = list.filter((p) => {
        const name = (p?.name || '').toLowerCase();
        const id = (p?.id || '').toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    }

    if (sort === 'name') {
      list.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    } else if (sort === 'oldest') {
      list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return list;
  }, [projects, query, sort, tab]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const p = await createProject(name, newPictureUrl.trim() || undefined);
      setNewName('');
      setNewPictureUrl('');
      setCreateOpen(false);
      toast.success('Project created');
      onOpenProject?.(p.id);
    } catch (e) {
      toast.error(e?.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      toast.success('Project deleted');
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const dialogPaperSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(accentPurple, 0.22)}`,
    background: `linear-gradient(165deg, ${alpha(accentPurple, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 48%, ${alpha(accentCyan, 0.05)} 100%)`,
    backdropFilter: 'blur(12px)',
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }} justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.5,
              backgroundImage: `linear-gradient(90deg, ${accentPurple} 0%, #6366f1 45%, ${accentCyan} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Create new projects and switch between your workspaces.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: 'stretch', sm: 'flex-end' }} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon sx={{ color: '#08090e' }} />}
            onClick={() => setCreateOpen(true)}
            sx={{
              ...gradientPrimarySx,
              py: 1,
              px: 2.5,
              minWidth: { xs: '100%', sm: 200 },
            }}
          >
            Create New Project
          </Button>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => refreshProjects()}
                disabled={loadingProjects}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha('#fff', 0.14)}`,
                  bgcolor: alpha('#fff', 0.03),
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(accentPurple, 0.22)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.88)} 42%, ${alpha(accentCyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack spacing={0}>
          <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  minHeight: 42,
                  '& .MuiTab-root': { fontWeight: 780, textTransform: 'none', letterSpacing: 0.2, minHeight: 42 },
                  '& .Mui-selected': { color: alpha('#ede9fe', 0.98) },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: 999,
                    bgcolor: accentPurple,
                    boxShadow: `0 0 16px ${alpha(accentPurple, 0.45)}`,
                  },
                }}
              >
                <Tab value="all" label="All projects" />
                <Tab value="recent" label="Recent" />
              </Tabs>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', md: 'auto' }, alignItems: { sm: 'center' } }}>
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search projects..."
                  size="small"
                  fullWidth
                  sx={{
                    minWidth: { md: 280 },
                    flex: { sm: '1 1 0%' },
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha('#000', 0.22) },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  sx={{
                    width: { xs: '100%', sm: 176 },
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha('#000', 0.22) },
                  }}
                >
                  <MenuItem value="newest">Date (Newest)</MenuItem>
                  <MenuItem value="oldest">Date (Oldest)</MenuItem>
                  <MenuItem value="name">Name (A-Z)</MenuItem>
                </TextField>
              </Stack>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: alpha('#fff', 0.06) }} />

          {loadingProjects ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, bgcolor: alpha('#fff', 0.06) }} />
              <Box sx={{ mt: 2 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={`row-${i}`} variant="rounded" height={52} sx={{ mb: 1.25, borderRadius: 2, bgcolor: alpha('#fff', 0.05) }} />
                ))}
              </Box>
            </Box>
          ) : filteredSorted.length === 0 ? (
            <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 400, mx: 'auto' }}>
                Try a different search, or create a new project.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 560, ...tableShellSx, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: '36%' }}>Project</TableCell>
                    <TableCell sx={{ ...headCellSx, width: '12%' }}>Status</TableCell>
                    <TableCell sx={{ ...headCellSx, width: '16%' }}>Created</TableCell>
                    <TableCell sx={{ ...headCellSx, width: '22%' }}>ID</TableCell>
                    <TableCell align="right" sx={{ ...headCellSx, width: '14%' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSorted.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => onOpenProject?.(p.id)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: alpha(accentPurple, 0.06),
                          boxShadow: `inset 0 0 0 1px ${alpha(accentPurple, 0.12)}`,
                        },
                        '&:nth-of-type(even) td': { bgcolor: alpha('#fff', 0.015) },
                        '& td': { py: 1.35, borderColor: alpha('#fff', 0.06) },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar
                            src={p.picture_url || p.pictureUrl || undefined}
                            alt={p.name}
                            sx={{
                              width: 38,
                              height: 38,
                              fontWeight: 800,
                              border: `1px solid ${alpha(accentPurple, 0.35)}`,
                              boxShadow: `0 0 12px ${alpha(accentPurple, 0.12)}`,
                              bgcolor: projectFallbackColor(p.id || p.name),
                            }}
                          >
                            {(p.name || '?').slice(0, 1).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 820 }} noWrap>
                              {p.name}
                            </Typography>
                            <ProjectSubtitleRow project={p} accentCyan={accentCyan} />
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {(p.launch_completed_at || p.launchCompletedAt) ? (
                          <Chip
                            label="Completed"
                            size="small"
                            sx={{
                              fontWeight: 780,
                              height: 26,
                              color: alpha('#6ee7b7', 0.98),
                              bgcolor: alpha('#22c55e', 0.14),
                              border: `1px solid ${alpha('#22c55e', 0.35)}`,
                              boxShadow: `0 0 12px ${alpha('#22c55e', 0.08)}`,
                            }}
                          />
                        ) : p.has_master_wallet ? (
                          <Chip
                            label="Ready"
                            size="small"
                            sx={{
                              fontWeight: 780,
                              height: 26,
                              color: alpha(accentCyan, 0.96),
                              bgcolor: alpha(accentCyan, 0.1),
                              border: `1px solid ${alpha(accentCyan, 0.32)}`,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Incomplete"
                            size="small"
                            sx={{
                              fontWeight: 780,
                              height: 26,
                              borderColor: alpha('#fff', 0.14),
                              bgcolor: alpha('#fff', 0.04),
                              color: alpha('#e9e7ff', 0.72),
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 650, color: alpha('#fff', 0.78) }}>
                          {formatDate(p.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', letterSpacing: 0.02, color: alpha('#fff', 0.52) }} noWrap title={p.id}>
                          {p.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FolderOpenIcon sx={{ fontSize: 18 }} />}
                            onClick={() => onOpenProject?.(p.id)}
                            sx={{
                              ...pillSx,
                              py: 0.55,
                              fontSize: '0.8125rem',
                              borderColor: alpha(accentPurple, 0.45),
                              color: alpha('#ede9ff', 0.95),
                              '&:hover': {
                                borderColor: alpha(accentCyan, 0.55),
                                bgcolor: alpha(accentPurple, 0.08),
                                boxShadow: `0 0 14px ${alpha(accentPurple, 0.12)}`,
                              },
                            }}
                          >
                            Open
                          </Button>
                          <Tooltip title="Delete project">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget({ id: p.id, name: p.name })}
                                sx={{
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                                  color: alpha(theme.palette.error.light, 0.95),
                                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Opening a project sets it as your active workspace across Wallet Management and launch tools.
      </Typography>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle sx={{ fontWeight: 880, pb: 0.5 }}>Create New Project</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Project name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Arb Sniper #3"
              autoFocus
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Picture URL (optional)"
              value={newPictureUrl}
              onChange={(e) => setNewPictureUrl(e.target.value)}
              placeholder="https://..."
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} variant="outlined" sx={{ ...pillSx, borderColor: alpha('#fff', 0.2) }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disableElevation
            startIcon={<AddIcon sx={{ color: '#08090e' }} />}
            disabled={creating || !newName.trim()}
            sx={{ ...gradientPrimarySx, py: 1 }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 880, pb: 0.5 }}>Delete Project</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete <strong>{deleteTarget?.name}</strong> and all of its data (wallets, settings, etc).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} variant="outlined" sx={{ ...pillSx, borderColor: alpha('#fff', 0.2) }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting} sx={{ ...pillSx, py: 1 }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
